/**
 * useRegistrazioneAudio — PRIMITIVA CANONICA di cattura audio dal microfono.
 *
 * ⚠️ Questa è la fonte unica: nel repo esistono 17 implementazioni indipendenti di
 * `new MediaRecorder(...)` e la duplicazione ha già prodotto difetti divergenti (su 12
 * punti la registrazione non funziona su iPhone/iPad, in 5 l'auto-stop non scatta mai,
 * in 6 il microfono resta acceso dopo la chiusura). Censimento, difetti con file e riga,
 * e ordine di migrazione: `docs/01-ARCHITECTURE/AUDIO-RECORDER-CONSOLIDAMENTO.md`.
 * NON aggiungere una diciottesima copia: estendi questa, o componi un wrapper sopra.
 *
 * ## Confine di responsabilità (regola di taglio, vincolante)
 * Questa primitiva possiede SOLO ciò che sta tra `getUserMedia` e il Blob: permessi,
 * negoziazione del formato, guard anti doppio-avvio, raccolta dei chunk, scarto del
 * vuoto, durata massima, tempo trascorso, annullamento, pulizia.
 *
 * Restano FUORI, in wrapper componibili sopra di essa:
 *  - upload su Storage, chiamate a Cloud Function, id pre-allocati (altrimenti la
 *    primitiva "condivisa" finirebbe per sapere di Firebase e non sarebbe condivisibile);
 *  - soglie di dimensione imposte dal backend (sono precondizioni di quella CF, non
 *    proprietà del microfono);
 *  - livelli audio/waveform reali, pausa/riprendi, stream che sopravvive allo stop:
 *    hanno risorse proprie (AudioContext, requestAnimationFrame) e vivono altrove;
 *  - il layer gestuale (tieni-premuto, swipe per annullare, blocco mani-libere): pilota
 *    il recorder, non è registrazione.
 *
 * ## Perché gli stati stanno negli EVENTI e non nei comandi
 * La versione precedente assumeva che l'unico modo di fermarsi fosse `ferma()`, e teneva
 * le transizioni nei comandi. Con tre stop NON comandati — durata massima raggiunta,
 * `annulla()`, tracce terminate dal sistema operativo — quella struttura lasciava
 * `elaborando` alto per sempre e `registrando` incoerente. Ora `onstart`/`onstop` sono
 * l'unica autorità sulla fase, e ogni percorso di uscita passa da un `finally`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// ============================================================================
// FORMATO
// ============================================================================

/** Candidati in ordine di preferenza. Safari/iOS non supporta webm → mp4. */
const CANDIDATI_MIME = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'] as const;

/**
 * Estensione di file corretta per un mimeType.
 *
 * ⚠️ Esportata perché serve a chi costruisce un percorso su Storage: diverse Cloud
 * Function deducono il formato dal NOME del file, quindi salvare un mp4 chiamato
 * `.webm` degrada la trascrizione **in silenzio**. Chi carica deve usare questa
 * funzione per il nome E il mimeType reale per il `contentType`.
 */
export function estensioneDaMime(mimeType: string): string {
  const base = (mimeType || '').split(';')[0].trim().toLowerCase();
  switch (base) {
    case 'audio/webm': return 'webm';
    case 'audio/mp4':
    case 'audio/x-m4a':
    case 'audio/m4a': return 'm4a';
    case 'audio/mpeg': return 'mp3';
    case 'audio/ogg': return 'ogg';
    case 'audio/wav':
    case 'audio/x-wav': return 'wav';
    default: return 'webm';
  }
}

/** Sceglie il primo formato che il browser dichiara di supportare. */
function scegliMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  for (const c of CANDIDATI_MIME) {
    try {
      if (MediaRecorder.isTypeSupported(c)) return c;
    } catch {
      /* isTypeSupported può lanciare su implementazioni vecchie */
    }
  }
  return undefined; // default nativo del browser (iOS → audio/mp4)
}

/** Blob → base64 puro (senza il prefisso `data:...;base64,`). */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const res = typeof reader.result === 'string' ? reader.result : '';
      const virgola = res.indexOf(',');
      resolve(virgola >= 0 ? res.slice(virgola + 1) : '');
    };
    reader.onerror = () => reject(reader.error ?? new Error('Lettura audio fallita'));
    reader.readAsDataURL(blob);
  });
}

// ============================================================================
// CONTRATTO
// ============================================================================

/** Fase del ciclo di registrazione. È la SSOT: i booleani sono derivati da qui. */
export type FaseRegistrazione = 'inattivo' | 'preparazione' | 'registrazione' | 'elaborazione';

/** Errori riconoscibili a programma, separati dal messaggio mostrato all'utente. */
export type CodiceErroreRegistrazione =
  | 'troppo_breve'
  | 'permesso_negato'
  | 'non_supportato'
  | 'elaborazione_fallita';

/** Dati dell'audio catturato, passati al sink insieme al Blob. */
export interface MetaAudio {
  /** Formato REALE prodotto dal recorder (su iOS è `audio/mp4`, non webm). */
  mimeType: string;
  /** Estensione coerente col mimeType, per i percorsi su Storage. */
  estensione: string;
  /**
   * Durata in secondi, calcolata da timestamp (non dal contatore a intervalli):
   * è l'unico valore autorevole, ed è quello da persistere.
   */
  durataSec: number;
  /** Momento di INIZIO registrazione: alcuni percorsi Storage lo usano nel nome. */
  timestampInizioMs: number;
  /** Conversione in base64, eseguita solo se il chiamante la chiede. */
  toBase64: () => Promise<string>;
}

/** Testi mostrati all'utente. Ogni schermata può sovrascriverli. */
export interface MessaggiRegistrazione {
  troppoBreve: string;
  permessoNegato: string;
  nonSupportato: string;
  elaborazioneFallita: string;
}

const MESSAGGI_DEFAULT: MessaggiRegistrazione = {
  troppoBreve: 'Registrazione troppo breve: riprova tenendo premuto un momento in più.',
  permessoNegato: 'Microfono non disponibile o permesso negato.',
  nonSupportato: 'Questo browser non permette di registrare audio.',
  elaborazioneFallita: 'Elaborazione audio non riuscita.',
};

export interface UseRegistrazioneAudioParams {
  /**
   * Riceve l'audio pronto. Il Blob è la forma primaria (serve a chi carica su
   * Storage); chi vuole il base64 chiama `meta.toBase64()` — così non si paga la
   * conversione quando non serve.
   */
  onAudioPronto: (blob: Blob, meta: MetaAudio) => void | Promise<void>;
  /**
   * Secondi oltre i quali fermarsi da sola. `0` = nessun limite.
   * ⚠️ Nelle implementazioni sparse questo limite era mostrato nella UI ma non
   * funzionava mai (chiusura obsoleta): qui l'auto-stop è nel recorder e scatta.
   */
  maxSecondi?: number;
  /**
   * Aggiorna `secondi` una volta al secondo. Default: attivo solo se c'è un limite
   * di durata (chi non mostra il contatore non paga un re-render al secondo).
   */
  contaSecondi?: boolean;
  /** Byte sotto i quali la registrazione è considerata vuota. Default 1024. */
  sogliaMinimaByte?: number;
  /** Chiamato quando la registrazione si ferma per durata massima raggiunta. */
  onLimiteRaggiunto?: () => void;
  /** Vincoli da passare a `getUserMedia`. Default: cancellazione eco e rumore. */
  vincoliAudio?: MediaTrackConstraints;
  /** Testi da sovrascrivere (parziale). */
  messaggi?: Partial<MessaggiRegistrazione>;
}

export interface RegistrazioneAudio {
  /** Fase corrente: è la sorgente di verità. */
  fase: FaseRegistrazione;
  /** Derivato da `fase`: microfono aperto. */
  registrando: boolean;
  /** Derivato da `fase`: audio in elaborazione (dallo stop alla fine del sink). */
  elaborando: boolean;
  /** Derivato da `fase`: qualunque cosa diversa da inattivo. Blocca la UI. */
  occupato: boolean;
  /** Secondi trascorsi (0 se il conteggio è disattivato). */
  secondi: number;
  /** true se il browser non permette di registrare. */
  nonSupportato: boolean;
  /** Messaggio pronto da mostrare. */
  errore: string | null;
  /** Codice dell'errore, per chi vuole scrivere un messaggio proprio. */
  codiceErrore: CodiceErroreRegistrazione | null;
  /** Avvia (richiede un gesto dell'utente per ottenere il permesso). */
  avvia: () => Promise<void>;
  /** Ferma e consegna l'audio al sink. */
  ferma: () => void;
  /** Ferma e BUTTA l'audio: il sink non viene mai chiamato. */
  annulla: () => void;
  /** Azzera l'errore. */
  resetErrore: () => void;
}

// ============================================================================
// HOOK
// ============================================================================

export function useRegistrazioneAudio({
  onAudioPronto,
  maxSecondi = 0,
  contaSecondi,
  sogliaMinimaByte = 1024,
  onLimiteRaggiunto,
  vincoliAudio,
  messaggi,
}: UseRegistrazioneAudioParams): RegistrazioneAudio {
  const [fase, setFase] = useState<FaseRegistrazione>('inattivo');
  const [secondi, setSecondi] = useState(0);
  const [errore, setErrore] = useState<string | null>(null);
  const [codiceErrore, setCodiceErrore] = useState<CodiceErroreRegistrazione | null>(null);

  const testi = useMemo(() => ({ ...MESSAGGI_DEFAULT, ...messaggi }), [messaggi]);
  // Il contatore serve per forza quando c'è un limite: senza, l'utente vedrebbe
  // la registrazione fermarsi da sola senza capire perché.
  const contaAttivo = contaSecondi ?? maxSecondi > 0;

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoStopRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inizioMsRef = useRef<number>(0);

  /**
   * Fase in forma sincrona: lo stato React arriva troppo tardi per fare da guardia.
   * Due dita su un tablet, o un doppio tap, aprirebbero due stream lasciando il
   * primo microfono acceso — è il difetto presente in 13 implementazioni su 14.
   */
  const faseRef = useRef<FaseRegistrazione>('inattivo');
  /** Alzata da `annulla()`: `onstop` la legge e non consegna nulla al sink. */
  const annullatoRef = useRef(false);
  /** Alzata se si chiede lo stop mentre si sta ancora ottenendo il permesso. */
  const stopRichiestoRef = useRef(false);
  /** true durante lo smontaggio: non si consegna audio a un componente morto. */
  const smontatoRef = useRef(false);

  /**
   * Sink e callback tenuti in ref "più recente": dentro `onstop` una closure
   * catturata all'avvio sarebbe obsoleta (difetto da cui `useSegnalazioneVoiceRecorder`
   * si difende già a mano). Tenerli qui li toglie anche dalle dipendenze di `avvia`.
   */
  const sinkRef = useRef(onAudioPronto);
  const onLimiteRef = useRef(onLimiteRaggiunto);
  useEffect(() => { sinkRef.current = onAudioPronto; }, [onAudioPronto]);
  useEffect(() => { onLimiteRef.current = onLimiteRaggiunto; }, [onLimiteRaggiunto]);

  const nonSupportato =
    typeof navigator === 'undefined' ||
    !navigator.mediaDevices?.getUserMedia ||
    typeof MediaRecorder === 'undefined';

  /** Cambia fase mantenendo allineata la guardia sincrona. */
  const vaiA = useCallback((prossima: FaseRegistrazione) => {
    faseRef.current = prossima;
    setFase(prossima);
  }, []);

  /** Spegne timer, auto-stop e tracce del microfono. Sempre sicura da chiamare. */
  const pulisci = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (autoStopRef.current) {
      clearTimeout(autoStopRef.current);
      autoStopRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const segnalaErrore = useCallback(
    (codice: CodiceErroreRegistrazione, messaggio: string) => {
      setCodiceErrore(codice);
      setErrore(messaggio);
    },
    [],
  );

  // ── AVVIO ────────────────────────────────────────────────────────────────

  const avvia = useCallback(async () => {
    // Guardia estesa a TUTTO il ciclo, non solo alla registrazione: durante
    // l'elaborazione (upload + trascrizione: secondi) il recorder è già inattivo,
    // e senza questo un secondo avvio partirebbe sopra la pipeline in corso.
    if (faseRef.current !== 'inattivo') return;
    if (nonSupportato) {
      segnalaErrore('non_supportato', testi.nonSupportato);
      return;
    }

    vaiA('preparazione');
    setErrore(null);
    setCodiceErrore(null);
    annullatoRef.current = false;
    stopRichiestoRef.current = false;

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: vincoliAudio ?? {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch {
      vaiA('inattivo');
      segnalaErrore('permesso_negato', testi.permessoNegato);
      return;
    }

    // Stop chiesto mentre si aspettava il permesso (tap rapido, tipico su touch):
    // chiudi e torna inattivo senza aver mai registrato.
    if (stopRichiestoRef.current || smontatoRef.current) {
      stream.getTracks().forEach((t) => t.stop());
      vaiA('inattivo');
      return;
    }

    streamRef.current = stream;
    const mimeScelto = scegliMimeType();

    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, mimeScelto ? { mimeType: mimeScelto } : undefined);
    } catch {
      // Formato rifiutato: è ESATTAMENTE ciò che accade su Safari/iOS quando il
      // mimeType è scritto fisso. Ultimo tentativo col default del browser.
      try {
        recorder = new MediaRecorder(stream);
      } catch {
        pulisci();
        vaiA('inattivo');
        segnalaErrore('non_supportato', testi.nonSupportato);
        return;
      }
    }

    chunksRef.current = [];
    recorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };

    // Alcuni browser non emettono `onstart`: questo flag di sessione rende il
    // gestore idempotente, così può essere invocato a mano dopo `start()` senza
    // rischio di eseguirlo due volte.
    let onStartEseguito = false;

    // `onstart` è l'unica autorità che dichiara "sto registrando".
    recorder.onstart = () => {
      if (onStartEseguito) return;
      onStartEseguito = true;
      inizioMsRef.current = Date.now();
      vaiA('registrazione');
      setSecondi(0);
      if (contaAttivo) {
        timerRef.current = setInterval(() => {
          setSecondi(Math.floor((Date.now() - inizioMsRef.current) / 1000));
        }, 1000);
      }
      if (maxSecondi > 0) {
        autoStopRef.current = setTimeout(() => {
          onLimiteRef.current?.();
          // Stop reale sul recorder: nessuna closure obsoleta di mezzo.
          if (recorderRef.current && recorderRef.current.state !== 'inactive') {
            recorderRef.current.stop();
          }
        }, maxSecondi * 1000);
      }
    };

    recorder.onstop = async () => {
      const durataSec = Math.max(0, Math.round((Date.now() - inizioMsRef.current) / 1000));
      const mimeType = recorder.mimeType || mimeScelto || 'audio/webm';
      const chunks = chunksRef.current;
      chunksRef.current = [];
      pulisci();

      // Annullato o smontato: si butta tutto, il sink non viene mai chiamato.
      if (annullatoRef.current || smontatoRef.current) {
        vaiA('inattivo');
        return;
      }

      const blob = new Blob(chunks, { type: mimeType });
      if (blob.size < sogliaMinimaByte) {
        vaiA('inattivo');
        segnalaErrore('troppo_breve', testi.troppoBreve);
        return;
      }

      // Un solo try/finally attorno all'intera consegna: qualunque cosa accada,
      // la fase torna a inattivo. È il difetto che bloccava il busy per sempre.
      vaiA('elaborazione');
      try {
        await sinkRef.current(blob, {
          mimeType,
          estensione: estensioneDaMime(mimeType),
          durataSec,
          timestampInizioMs: inizioMsRef.current,
          toBase64: () => blobToBase64(blob),
        });
      } catch (e) {
        segnalaErrore(
          'elaborazione_fallita',
          e instanceof Error && e.message ? e.message : testi.elaborazioneFallita,
        );
      } finally {
        vaiA('inattivo');
      }
    };

    try {
      recorder.start();
      // Se il browser non ha emesso `onstart`, lo eseguiamo noi: senza, la fase
      // resterebbe in 'preparazione' e il timer non partirebbe mai.
      if (!onStartEseguito) recorder.onstart?.(new Event('start'));
    } catch {
      pulisci();
      vaiA('inattivo');
      segnalaErrore('non_supportato', testi.nonSupportato);
    }
  }, [
    nonSupportato, vincoliAudio, testi, maxSecondi, contaAttivo,
    sogliaMinimaByte, pulisci, vaiA, segnalaErrore,
  ]);

  // ── STOP / ANNULLA ───────────────────────────────────────────────────────

  const ferma = useCallback(() => {
    // Se il permesso è ancora in corso, l'avvio se ne accorgerà e rinuncerà.
    if (faseRef.current === 'preparazione') {
      stopRichiestoRef.current = true;
      return;
    }
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      // La fase la cambia `onstop`: qui non si anticipa nulla.
      rec.stop();
    }
  }, []);

  /**
   * Ferma buttando l'audio. Stacca ANCHE i gestori del recorder, non solo alza il
   * flag: è ciò che fanno oggi le implementazioni con il cestino, e sostituirlo con
   * il solo flag lascerebbe girare un `onstop` postumo su una sessione già chiusa.
   */
  const annulla = useCallback(() => {
    annullatoRef.current = true;
    if (faseRef.current === 'preparazione') {
      stopRichiestoRef.current = true;
      return;
    }
    const rec = recorderRef.current;
    if (rec) {
      rec.ondataavailable = null;
      rec.onstop = null;
      rec.onstart = null;
      try {
        if (rec.state !== 'inactive') rec.stop();
      } catch {
        /* già inattivo */
      }
    }
    recorderRef.current = null;
    chunksRef.current = [];
    pulisci();
    vaiA('inattivo');
  }, [pulisci, vaiA]);

  // ── SMONTAGGIO ───────────────────────────────────────────────────────────

  /**
   * Allo smontaggio si chiude il microfono e si BUTTA l'audio: consegnarlo
   * significherebbe far partire una chiamata AI e aggiornare uno stato di un
   * componente che non esiste più.
   */
  useEffect(() => {
    smontatoRef.current = false;
    return () => {
      smontatoRef.current = true;
      const rec = recorderRef.current;
      if (rec) {
        rec.ondataavailable = null;
        rec.onstop = null;
        rec.onstart = null;
        try {
          if (rec.state !== 'inactive') rec.stop();
        } catch {
          /* ignore */
        }
      }
      recorderRef.current = null;
      pulisci();
    };
  }, [pulisci]);

  return {
    fase,
    registrando: fase === 'registrazione',
    elaborando: fase === 'elaborazione',
    occupato: fase !== 'inattivo',
    secondi,
    nonSupportato,
    errore,
    codiceErrore,
    avvia,
    ferma,
    annulla,
    resetErrore: () => {
      setErrore(null);
      setCodiceErrore(null);
    },
  };
}

export default useRegistrazioneAudio;
